import { FC } from 'react';
import { ThemeProvider } from '@mui/material';

import { useTranslation } from '@mopla/i18';
import { passengerThemeV2 } from '@mopla/mui';

import { SwapLocationsIcon } from '../../icons';

import { AddressLineView } from './ui/AddressLineView';
import { PointIcon } from './ui/PointIcon';
import {
	ConnectorSection,
	DashedLine,
	DashedLineContainer,
	HorizontalSeparator,
	LocationRow,
	RouteInputContainer,
	SwapButton,
} from './styled';
import { TRouteInputValue } from './types';

export interface IMplRouteInputProps {
	/** From location object */
	fromValue?: TRouteInputValue;
	/** To location object */
	toValue?: TRouteInputValue;
	/** Callback when from field is clicked */
	onFromClick?: () => void;
	/** Callback when to field is clicked */
	onToClick?: () => void;
	/** Callback when swap button is clicked */
	onSwapClick?: () => void;
	/** Turns on pulsating the location dot */
	isGeoWatching?: boolean;
	/** Show blue outline dot while geo position is loading */
	isGeoInitialLoading?: boolean;
	/** Additional class name for external styling */
	className?: string;
	departureTestId?: string;
	arrivalTestId?: string;
	swapTestId?: string;
}

export const MplRouteInput: FC<IMplRouteInputProps> = ({
	fromValue,
	toValue,
	onFromClick,
	onToClick,
	onSwapClick,
	isGeoWatching,
	isGeoInitialLoading,
	className,
	departureTestId = 'routeInput-departure-row',
	arrivalTestId = 'routeInput-arrival-row',
	swapTestId = 'routeInput-swap-button',
}) => {
	const { t } = useTranslation('searchForm');

	return (
		<ThemeProvider theme={passengerThemeV2}>
			<RouteInputContainer className={className}>
				{/* Departure row */}
				<LocationRow onClick={onFromClick} data-testid={departureTestId}>
					<PointIcon
						location={fromValue}
						size="small"
						pulsating={isGeoWatching}
						isGeoInitialLoading={isGeoInitialLoading}
					/>
					<AddressLineView
						value={fromValue}
						placeholder={t('rideHailingV2.departurePlaceholder')}
					/>
				</LocationRow>

				{/* Connector section */}
				<ConnectorSection>
					<DashedLineContainer>
						<DashedLine />
					</DashedLineContainer>
					<HorizontalSeparator />
					<SwapButton
						onClick={onSwapClick}
						aria-label="Swap locations"
						data-testid={swapTestId}
					>
						<SwapLocationsIcon />
					</SwapButton>
				</ConnectorSection>

				{/* Arrival row */}
				<LocationRow onClick={onToClick} data-testid={arrivalTestId}>
					<PointIcon
						location={toValue}
						size="small"
						pulsating={isGeoWatching}
					/>
					<AddressLineView
						value={toValue}
						placeholder={t('rideHailingV2.arrivalPlaceholder')}
					/>
				</LocationRow>
			</RouteInputContainer>
		</ThemeProvider>
	);
};
